import React, { useMemo } from "react";
import Icon from "../../../components/AppIcon";

const TroncTakingsChart = ({ troncRecords, locationFilter = null }) => {
  // Filter records by location if specified
  const filteredRecords = useMemo(() => {
    if (!troncRecords || troncRecords.length === 0) return [];

    let records = troncRecords;
    if (locationFilter) {
      records = records.filter((r) => r.locationId === locationFilter);
    }

    // Get last 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    return records
      .filter((r) => new Date(r.periodStart) >= thirtyDaysAgo)
      .sort((a, b) => new Date(a.periodStart) - new Date(b.periodStart));
  }, [troncRecords, locationFilter]);

  // Group by date and sum amounts
  const dailyData = useMemo(() => {
    const dataMap = new Map();

    filteredRecords.forEach((record) => {
      const date = new Date(record.periodStart).toISOString().split("T")[0];
      const existing = dataMap.get(date) || 0;
      dataMap.set(date, existing + (record.amount || 0));
    });

    // Convert to array and sort
    return Array.from(dataMap.entries())
      .map(([date, amount]) => ({ date, amount }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [filteredRecords]);

  // Calculate stats
  const totalAmount = dailyData.reduce((sum, d) => sum + d.amount, 0);
  const averageAmount =
    dailyData.length > 0 ? totalAmount / dailyData.length : 0;
  const maxAmount = Math.max(...dailyData.map((d) => d.amount), 0);
  const minAmount =
    dailyData.length > 0 ? Math.min(...dailyData.map((d) => d.amount)) : 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  // Calculate SVG line chart points
  const chartHeight = 200;
  const chartWidth = 600;
  const padding = { top: 40, right: 20, bottom: 30, left: 60 };
  const graphWidth = chartWidth - padding.left - padding.right;
  const graphHeight = chartHeight - padding.top - padding.bottom;

  const linePoints = useMemo(() => {
    if (dailyData.length === 0) return "";

    const xStep = dailyData.length > 1 ? graphWidth / (dailyData.length - 1) : graphWidth / 2;
    const yScale = maxAmount > 0 ? graphHeight / maxAmount : 1;

    return dailyData.map((d, i) => {
      const x = padding.left + (dailyData.length > 1 ? i * xStep : graphWidth / 2);
      const y = padding.top + graphHeight - (d.amount * yScale);
      return `${x},${y}`;
    }).join(" ");
  }, [dailyData, graphWidth, graphHeight, maxAmount]);

  const areaPoints = useMemo(() => {
    if (dailyData.length === 0) return "";

    const xStep = dailyData.length > 1 ? graphWidth / (dailyData.length - 1) : graphWidth / 2;
    const yScale = maxAmount > 0 ? graphHeight / maxAmount : 1;

    const topPoints = dailyData.map((d, i) => {
      const x = padding.left + (dailyData.length > 1 ? i * xStep : graphWidth / 2);
      const y = padding.top + graphHeight - (d.amount * yScale);
      return `${x},${y}`;
    });

    // Close the area path
    const firstX = padding.left;
    const lastX = padding.left + (dailyData.length > 1 ? (dailyData.length - 1) * xStep : graphWidth / 2);
    const bottomY = padding.top + graphHeight;

    return `${firstX},${bottomY} ${topPoints.join(" ")} ${lastX},${bottomY}`;
  }, [dailyData, graphWidth, graphHeight, maxAmount]);

  if (dailyData.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="TrendingUp" size={24} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-xl font-semibold">TRONC Takings Trend</h3>
            <p className="text-sm text-muted-foreground">Last 30 days</p>
          </div>
        </div>

        <div className="text-center py-12">
          <Icon
            name="TrendingUp"
            size={48}
            className="mx-auto mb-4 text-muted-foreground opacity-50"
          />
          <p className="text-muted-foreground">
            No data available for the selected period
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="TrendingUp" size={24} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-xl font-semibold">TRONC Takings Trend</h3>
            <p className="text-sm text-muted-foreground">Last 30 days</p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="text-xs text-muted-foreground mb-1">Total</p>
          <p className="text-lg font-bold text-foreground">
            {formatCurrency(totalAmount)}
          </p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="text-xs text-muted-foreground mb-1">Average</p>
          <p className="text-lg font-bold text-foreground">
            {formatCurrency(averageAmount)}
          </p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="text-xs text-muted-foreground mb-1">Highest</p>
          <p className="text-lg font-bold text-success">
            {formatCurrency(maxAmount)}
          </p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="text-xs text-muted-foreground mb-1">Lowest</p>
          <p className="text-lg font-bold text-foreground">
            {formatCurrency(minAmount)}
          </p>
        </div>
      </div>

      {/* SVG Line Chart */}
      <div className="relative w-full overflow-x-auto">
        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          className="w-full h-64"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
            const y = padding.top + graphHeight * (1 - ratio);
            return (
              <g key={i}>
                <line
                  x1={padding.left}
                  y1={y}
                  x2={chartWidth - padding.right}
                  y2={y}
                  stroke="currentColor"
                  strokeOpacity={0.1}
                  strokeDasharray="4,4"
                />
                <text
                  x={padding.left - 8}
                  y={y + 4}
                  textAnchor="end"
                  className="text-[10px] fill-muted-foreground"
                >
                  {formatCurrency(maxAmount * ratio)}
                </text>
              </g>
            );
          })}

          {/* X-axis labels */}
          {dailyData.map((d, i) => {
            if (i % Math.ceil(dailyData.length / 6) !== 0 && i !== dailyData.length - 1) return null;
            const xStep = dailyData.length > 1 ? graphWidth / (dailyData.length - 1) : graphWidth / 2;
            const x = padding.left + (dailyData.length > 1 ? i * xStep : graphWidth / 2);
            return (
              <text
                key={i}
                x={x}
                y={chartHeight - 8}
                textAnchor="middle"
                className="text-[10px] fill-muted-foreground"
              >
                {formatDate(d.date)}
              </text>
            );
          })}

          {/* Area fill */}
          <polygon
            points={areaPoints}
            fill="url(#gradient)"
            opacity={0.3}
          />

          {/* Line */}
          <polyline
            points={linePoints}
            fill="none"
            stroke="var(--color-primary)"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Data points */}
          {dailyData.map((d, i) => {
            const xStep = dailyData.length > 1 ? graphWidth / (dailyData.length - 1) : graphWidth / 2;
            const yScale = maxAmount > 0 ? graphHeight / maxAmount : 1;
            const x = padding.left + (dailyData.length > 1 ? i * xStep : graphWidth / 2);
            const y = padding.top + graphHeight - (d.amount * yScale);
            const tooltipAbove = y > padding.top + 50;
            const tooltipY = tooltipAbove ? y - 45 : y + 10;
            return (
              <g key={i} className="group cursor-pointer">
                <circle
                  cx={x}
                  cy={y}
                  r={5}
                  fill="var(--color-primary)"
                  stroke="var(--color-card)"
                  strokeWidth={2}
                  className="transition-all hover:r-7"
                />
                {/* Tooltip */}
                <g className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <rect
                    x={x - 45}
                    y={tooltipY}
                    width={90}
                    height={36}
                    rx={4}
                    fill="var(--color-foreground)"
                  />
                  <text
                    x={x}
                    y={tooltipY + 15}
                    textAnchor="middle"
                    className="text-xs font-semibold fill-background"
                  >
                    {formatCurrency(d.amount)}
                  </text>
                  <text
                    x={x}
                    y={tooltipY + 29}
                    textAnchor="middle"
                    className="text-[10px] fill-background opacity-70"
                  >
                    {formatDate(d.date)}
                  </text>
                </g>
              </g>
            );
          })}

          {/* Gradient definition */}
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="var(--color-primary)" stopOpacity={0.4} />
              <stop offset="100%" stopColor="var(--color-primary)" stopOpacity={0} />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-primary"></div>
          <span className="text-muted-foreground">Daily TRONC Amount</span>
        </div>
      </div>
    </div>
  );
};

export default TroncTakingsChart;
